import React, { useMemo, useState, useEffect } from 'react';
import { Card, Button } from 'react-bootstrap';
import classnames from 'classnames';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { toast } from '../../toasts';
import moment from 'moment';
import 'moment/locale/es';
import { getError } from '../../utils';
import CalendarFilters from './CalendarFilters';
import CalendarGrid from './CalendarGrid';
import CalendarDayModal from './CalendarDayModal';
import CalendarDetailPanel from './CalendarDetailPanel';
import GoogleEventModal from './GoogleEventModal';
import AppEventDateModal from './AppEventDateModal';
import { processEvents } from './calendarUtils';
import { CATEGORY_META } from './calendarEventTypes';
import ContractService from '../../services/contract.service';
import AssetSaleService from '../../services/assetSale.service';
import GeneratedDebtService from '../../services/generatedDebts.service';
import GeneratedCreditService from '../../services/generatedCredits.service';
import BillService from '../../services/bill.service';
import GoogleCalendarService from '../../services/googleCalendar.service';
import { useLabels } from '../../hooks/useLabels';
import './Calendar.css';

/**
 * Calendar component that displays events from:
 * - Rental contracts (expirations, closings, initiations)
 * - Rental dues (pending and overdue, unpaid only)
 * - Debt dues (pending and overdue, unpaid only)
 * - Credit dues (pending and overdue, unpaid only)
 * - Asset sales
 * - Bill expirations (pending and overdue, unpaid only)
 */
function CalendarView({ dashboardModules = ['assets', 'contracts', 'debts', 'credits', 'accounts', 'facturacion', 'income', 'calendar'] }) {
	const { t } = useLabels();
	moment.locale('es');
	// Month is the only reachable view now that the Vista toggle is gone
	// (Phase 9) — the setter is intentionally unused; week/day code paths
	// stay in place, just unreachable from this screen for now.
	const [viewMode] = useState('month');
	const [selectedDate, setSelectedDate] = useState(moment.utc().startOf('day'));
	const [selectedDay, setSelectedDay] = useState(null);
	const [showDayModal, setShowDayModal] = useState(false);
	const [dayModalEvents, setDayModalEvents] = useState([]);
	const [dayModalDate, setDayModalDate] = useState(null);
	
	const [contracts, setContracts] = useState([]);
	const [generatedDebts, setGeneratedDebts] = useState([]);
	const [generatedCredits, setGeneratedCredits] = useState([]);
	const [assetSales, setAssetSales] = useState([]);
	const [bills, setBills] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	// Google Calendar integration — connection/sync settings live in
	// GoogleCalendarConfiguration (AdminConfigScreen); this component only
	// reads the connection state to display events and create new ones.
	const [googleConnected, setGoogleConnected] = useState(false);
	// Seeded to the same 'primary' fallback the status effect resolves to —
	// starting at null caused every mount (now at t=0, since the overlay no
	// longer delays it) to fire the full calendar fetch twice: once with the
	// null placeholder, again once getStatus() resolves.
	const [selectedCalendarId, setSelectedCalendarId] = useState('primary');
	const [googleEvents, setGoogleEvents] = useState([]);
	const [showGoogleEventModal, setShowGoogleEventModal] = useState(false);
	const [editingGoogleEvent, setEditingGoogleEvent] = useState(null);
	const [createEventDefaultDate, setCreateEventDefaultDate] = useState(null);
	const [showAppEventDateModal, setShowAppEventDateModal] = useState(false);
	const [editingAppEvent, setEditingAppEvent] = useState(null);
	const [refreshKey, setRefreshKey] = useState(0);

	const [eventTypeFilters, setEventTypeFilters] = useState(() => ({
		'contract-expiration': dashboardModules.includes('contracts'),
		'contract-close': dashboardModules.includes('contracts'),
		'tenant-income': dashboardModules.includes('contracts') || dashboardModules.includes('income'),
		'owner-income': dashboardModules.includes('assets') || dashboardModules.includes('income'),
		'rental-due-expiration': dashboardModules.includes('contracts'),
		'rental-due-overdue': dashboardModules.includes('contracts'),
		'debt-due-expiration': dashboardModules.includes('debts'),
		'debt-due-overdue': dashboardModules.includes('debts'),
		'credit-due-expiration': dashboardModules.includes('credits'),
		'credit-due-overdue': dashboardModules.includes('credits'),
		'bill-expiration': dashboardModules.includes('assets'),
		'bill-overdue': dashboardModules.includes('assets'),
		'google-event': false,
	}));

	// Update filters when dashboardModules changes
	useEffect(() => {
		setEventTypeFilters(prev => {
			const newFilters = { ...prev };
			const hasContracts = dashboardModules.includes('contracts');
			const hasIncome = dashboardModules.includes('income');
			const hasAssets = dashboardModules.includes('assets');
			const hasDebts = dashboardModules.includes('debts');
			const hasCredits = dashboardModules.includes('credits');
			
			if (hasContracts) {
				if (prev['contract-expiration'] !== false) newFilters['contract-expiration'] = true;
				if (prev['contract-close'] !== false) newFilters['contract-close'] = true;
				if (prev['rental-due-expiration'] !== false) newFilters['rental-due-expiration'] = true;
				if (prev['rental-due-overdue'] !== false) newFilters['rental-due-overdue'] = true;
			} else {
				newFilters['contract-expiration'] = false;
				newFilters['contract-close'] = false;
				newFilters['rental-due-expiration'] = false;
				newFilters['rental-due-overdue'] = false;
			}
			
			if (hasContracts || hasIncome) {
				if (prev['tenant-income'] !== false) newFilters['tenant-income'] = true;
			} else {
				newFilters['tenant-income'] = false;
			}
			
			if (hasAssets || hasIncome) {
				if (prev['owner-income'] !== false) newFilters['owner-income'] = true;
			} else {
				newFilters['owner-income'] = false;
			}
			
			if (hasDebts) {
				if (prev['debt-due-expiration'] !== false) newFilters['debt-due-expiration'] = true;
				if (prev['debt-due-overdue'] !== false) newFilters['debt-due-overdue'] = true;
			} else {
				newFilters['debt-due-expiration'] = false;
				newFilters['debt-due-overdue'] = false;
			}
			
			if (hasCredits) {
				if (prev['credit-due-expiration'] !== false) newFilters['credit-due-expiration'] = true;
				if (prev['credit-due-overdue'] !== false) newFilters['credit-due-overdue'] = true;
			} else {
				newFilters['credit-due-expiration'] = false;
				newFilters['credit-due-overdue'] = false;
			}
			
			if (hasAssets) {
				if (prev['bill-expiration'] !== false) newFilters['bill-expiration'] = true;
				if (prev['bill-overdue'] !== false) newFilters['bill-overdue'] = true;
			} else {
				newFilters['bill-expiration'] = false;
				newFilters['bill-overdue'] = false;
			}
			
			return newFilters;
		});
	}, [dashboardModules]);

	// Check Google Calendar connection status. Connecting/disconnecting and sync
	// settings are managed in GoogleCalendarConfiguration (AdminConfigScreen) —
	// this just reflects the current state to know whether to fetch/show events.
	useEffect(() => {
		let isMounted = true;

		const refreshGoogleStatus = async () => {
			try {
				const { data } = await GoogleCalendarService.getStatus();
				if (!isMounted) return;
				setGoogleConnected(!!data.connected);
				setSelectedCalendarId(data.selectedCalendarId || 'primary');
			} catch (err) {
				if (isMounted) console.error('Error fetching Google Calendar status:', err);
			}
		};

		refreshGoogleStatus();

		return () => {
			isMounted = false;
		};
	}, []);

	// Enable/disable the Google event filter as connection state changes
	useEffect(() => {
		setEventTypeFilters(prev => ({ ...prev, 'google-event': googleConnected }));
	}, [googleConnected]);

	// Load data when changing month/week/day.
	// Keyed on dashboardModulesKey (a joined string) rather than the
	// dashboardModules array itself — Dashboard.jsx passes a fresh array
	// identity on every settings fetch/fallback, which would otherwise
	// refetch the whole calendar even when the enabled modules didn't change.
	const dashboardModulesKey = dashboardModules.join(',');
	useEffect(() => {
		let isMounted = true;

		const fetchCalendarData = async () => {
			if (!isMounted) return;
			
			try {
				if (!isMounted) return;
				setLoading(true);
				setError('');

				// Calculate date range based on view mode
				let dateRange;
				if (viewMode === 'month') {
					const monthStart = moment.utc(selectedDate).startOf('month');
					const monthEnd = moment.utc(selectedDate).endOf('month');
					dateRange = {
						start: monthStart,
						end: monthEnd,
					};
				} else if (viewMode === 'week') {
					const weekStart = moment.utc(selectedDate).startOf('week');
					const weekEnd = moment.utc(selectedDate).endOf('week');
					dateRange = {
						start: weekStart,
						end: weekEnd,
					};
				} else {
					// Daily view: fetch week from backend, filter to day in frontend
					const weekStart = moment.utc(selectedDate).startOf('week');
					const weekEnd = moment.utc(selectedDate).endOf('week');
					dateRange = {
						start: weekStart,
						end: weekEnd,
					};
				}
				const startDate = dateRange.start.format('YYYY-MM-DD');
				const endDate = dateRange.end.format('YYYY-MM-DD');
				
				// Search unpaid debt dues in this range — allData=true so calendar gets full set
				const generatedDebtsParams = new URLSearchParams();
				generatedDebtsParams.set('paid', 'no');
				generatedDebtsParams.set('expirationFrom', startDate);
				generatedDebtsParams.set('expirationTo', endDate);
				generatedDebtsParams.set('allData', 'true');

				// Search unpaid credit dues in this range — allData=true so calendar gets full set
				const generatedCreditsParams = new URLSearchParams();
				generatedCreditsParams.set('paid', 'no');
				generatedCreditsParams.set('expirationFrom', startDate);
				generatedCreditsParams.set('expirationTo', endDate);
				generatedCreditsParams.set('allData', 'true');

				// Search contracts in this range
				// Backend uses AND between periodInit and periodEnd, so we need separate queries
				// to catch contracts that started outside this range but end/close within it
				const contractsParamsPeriodInit = new URLSearchParams();
				contractsParamsPeriodInit.set('periodInitFrom', startDate);
				contractsParamsPeriodInit.set('periodInitTo', endDate);
				contractsParamsPeriodInit.set('allData', 'true');

				const contractsParamsPeriodEnd = new URLSearchParams();
				contractsParamsPeriodEnd.set('periodEndFrom', startDate);
				contractsParamsPeriodEnd.set('periodEndTo', endDate);
				contractsParamsPeriodEnd.set('allData', 'true');

				// Search asset sales in this range — allData=true so calendar gets full set
				const assetSalesParams = new URLSearchParams();
				assetSalesParams.set('dateFrom', startDate);
				assetSalesParams.set('dateTo', endDate);
				assetSalesParams.set('allData', 'true');

				// Search bills in this range
				const billsParams = new URLSearchParams();
				billsParams.set('expirationFrom', startDate);
				billsParams.set('expirationTo', endDate);
				billsParams.set('paid', 'no');

				// Search Google Calendar events in this range (only if connected)
				const googleEventsParams = new URLSearchParams();
				googleEventsParams.set('calendarId', selectedCalendarId || 'primary');
				googleEventsParams.set('timeMin', dateRange.start.toISOString());
				googleEventsParams.set('timeMax', dateRange.end.toISOString());

				const promises = [];
				const promiseIndices = {
					contracts: -1,
					contractsPeriodEnd: -1,
					generatedDebts: -1,
					generatedCredits: -1,
					bills: -1,
					assetSales: -1,
					googleEvents: -1,
				};

				// Only fetch data for enabled modules
				if (dashboardModules.includes('contracts') || dashboardModules.includes('income')) {
					promiseIndices.contracts = promises.length;
					promises.push(ContractService.search(contractsParamsPeriodInit.toString()));

					promiseIndices.contractsPeriodEnd = promises.length;
					promises.push(ContractService.search(contractsParamsPeriodEnd.toString()));
				}

				if (dashboardModules.includes('debts') || dashboardModules.includes('contracts')) {
					promiseIndices.generatedDebts = promises.length;
					// Owner/contract info comes populated on generatedDebt.debt, no extra fetch needed
					promises.push(GeneratedDebtService.search(generatedDebtsParams.toString()));
				}

				if (dashboardModules.includes('credits')) {
					promiseIndices.generatedCredits = promises.length;
					// Owner info comes populated on generatedCredit.credit, no extra fetch needed
					promises.push(GeneratedCreditService.search(generatedCreditsParams.toString()));
				}

				if (dashboardModules.includes('assets')) {
					promiseIndices.bills = promises.length;
					promises.push(BillService.search(billsParams.toString()));
				}

				if (dashboardModules.includes('assets') || dashboardModules.includes('income')) {
					promiseIndices.assetSales = promises.length;
					promises.push(AssetSaleService.search(assetSalesParams.toString()));
				}

				if (googleConnected) {
					promiseIndices.googleEvents = promises.length;
					promises.push(GoogleCalendarService.listEvents(googleEventsParams.toString()));
				}

				// allSettled: one failing endpoint shouldn't blank the whole calendar
				const settled = await Promise.allSettled(promises);

				if (!isMounted) return;

				const getData = (index) => {
					const outcome = settled[index];
					if (!outcome || outcome.status !== 'fulfilled') {
						if (outcome?.status === 'rejected') {
							console.error('Error fetching calendar data segment:', outcome.reason);
						}
						return undefined;
					}
					return outcome.value?.data;
				};

				let resultIndex = 0;
				if (promiseIndices.contracts !== -1) {
					const responseData = getData(resultIndex) || {};
					const contractsDataPeriodInit = responseData.contracts || responseData.allContracts || responseData || [];
					resultIndex++;

					if (promiseIndices.contractsPeriodEnd !== -1) {
						const responseDataPeriodEnd = getData(resultIndex) || {};
						const contractsDataPeriodEnd = responseDataPeriodEnd.contracts || responseDataPeriodEnd.allContracts || responseDataPeriodEnd || [];
						resultIndex++;

						// Combine both arrays and remove duplicates by _id
						const contractsMap = new Map();
						[...(Array.isArray(contractsDataPeriodInit) ? contractsDataPeriodInit : []),
							...(Array.isArray(contractsDataPeriodEnd) ? contractsDataPeriodEnd : [])].forEach(contract => {
							if (contract && contract._id) {
								contractsMap.set(contract._id.toString(), contract);
							}
						});
						setContracts(Array.from(contractsMap.values()));
					} else {
						setContracts(Array.isArray(contractsDataPeriodInit) ? contractsDataPeriodInit : []);
					}
				} else {
					setContracts([]);
				}

				if (promiseIndices.generatedDebts !== -1) {
					const data = getData(resultIndex);
					setGeneratedDebts(data?.allDebtsDues || data?.debtsDues || data || []);
					resultIndex++;
				} else {
					setGeneratedDebts([]);
				}

				if (promiseIndices.generatedCredits !== -1) {
					const data = getData(resultIndex);
					setGeneratedCredits(data?.allCreditsDues || data?.creditsDues || data || []);
					resultIndex++;
				} else {
					setGeneratedCredits([]);
				}

				if (promiseIndices.bills !== -1) {
					setBills(getData(resultIndex) || []);
					resultIndex++;
				} else {
					setBills([]);
				}

				if (promiseIndices.assetSales !== -1) {
					const responseData = getData(resultIndex) || {};
					const assetSalesData = responseData.assetSales || responseData.allAssetSales || responseData || [];
					setAssetSales(Array.isArray(assetSalesData) ? assetSalesData : []);
					resultIndex++;
				} else {
					setAssetSales([]);
				}

				if (promiseIndices.googleEvents !== -1) {
					const data = getData(resultIndex);
					setGoogleEvents(Array.isArray(data) ? data : []);
					resultIndex++;
				} else {
					setGoogleEvents([]);
				}

			} catch (err) {
				if (!isMounted) return;
				setError(getError(err));
				console.error('Error fetching calendar data:', err);
			} finally {
				if (isMounted) {
					setLoading(false);
				}
			}
		};

		fetchCalendarData();
		
		return () => {
			isMounted = false;
		};
	}, [viewMode, selectedDate, dashboardModulesKey, googleConnected, selectedCalendarId, refreshKey]);

	// Process events from data
	const allEvents = useMemo(() => {
		const events = processEvents({
			contracts: contracts || [],
			generatedDebts: generatedDebts || [],
			generatedCredits: generatedCredits || [],
			assetSales: assetSales || [],
			bills: bills || [],
			googleEvents: googleEvents || [],
			dashboardModules: dashboardModules,
		});
		return events;
	}, [contracts, generatedDebts, generatedCredits, assetSales, bills, googleEvents, dashboardModules]);

	// Filter events based on selected filters
	const filteredEvents = useMemo(() => {
		let filtered = allEvents;

		// Filter by event type (if filter is false, exclude the event)
		filtered = filtered.filter(event => eventTypeFilters[event.type] !== false);

		// Filter by view mode
		if (viewMode === 'month') {
			const monthStart = moment.utc(selectedDate).startOf('month');
			const monthEnd = moment.utc(selectedDate).endOf('month');
			filtered = filtered.filter(event => {
				const eventDate = moment.utc(event.date);
				return eventDate.isSameOrAfter(monthStart, 'day') && eventDate.isSameOrBefore(monthEnd, 'day');
			});
		} else if (viewMode === 'week') {
			const weekStart = moment.utc(selectedDate).startOf('week');
			const weekEnd = moment.utc(selectedDate).endOf('week');
			filtered = filtered.filter(event => {
				const eventDate = moment.utc(event.date);
				return eventDate.isSameOrAfter(weekStart, 'day') && eventDate.isSameOrBefore(weekEnd, 'day');
			});
		} else if (viewMode === 'day') {
			// Daily view: use same logic as weekly view but filter to single day
			const dayStart = moment.utc(selectedDate).startOf('day');
			const dayEnd = moment.utc(selectedDate).endOf('day');
			filtered = filtered.filter(event => {
				const eventDate = moment.utc(event.date);
				return eventDate.isSameOrAfter(dayStart, 'day') && eventDate.isSameOrBefore(dayEnd, 'day');
			});
		}

		return filtered;
	}, [allEvents, eventTypeFilters, selectedDate, viewMode]);

	// Group events by day
	const eventsByDay = useMemo(() => {
		const grouped = {};
		filteredEvents.forEach(event => {
			// Extract UTC components correctly from Date objects
			let eventDate;
			if (event.date instanceof Date) {
				eventDate = moment.utc([
					event.date.getUTCFullYear(),
					event.date.getUTCMonth(),
					event.date.getUTCDate()
				]);
			} else {
				eventDate = moment.utc(event.date);
			}
			const dayKey = eventDate.format('YYYY-MM-DD');
			if (!grouped[dayKey]) {
				grouped[dayKey] = [];
			}
			grouped[dayKey].push(event);
		});
		return grouped;
	}, [filteredEvents]);

	// Helper: Adjust date based on view mode
	const adjustDateForViewMode = (date, mode) => {
		if (!date) return moment.utc().startOf('day');
		const dateMoment = moment.isMoment(date) ? date.clone() : moment.utc(date);
		if (mode === 'month') return dateMoment.startOf('month');
		if (mode === 'week') return dateMoment.startOf('week');
		if (mode === 'day') return dateMoment.startOf('day');
		return dateMoment;
	};

	// Helper: Navigate date based on mode
	const navigateDate = (date, direction, mode) => {
		const dateMoment = moment.isMoment(date) ? date.clone() : moment.utc(date);
		if (mode === 'month') {
			return direction === 'prev' 
				? dateMoment.clone().subtract(1, 'month').startOf('month')
				: dateMoment.clone().add(1, 'month').startOf('month');
		}
		if (mode === 'week') {
			return direction === 'prev'
				? dateMoment.clone().subtract(1, 'week').startOf('week')
				: dateMoment.clone().add(1, 'week').startOf('week');
		}
		if (mode === 'day') {
			return direction === 'prev'
				? dateMoment.clone().subtract(1, 'day').startOf('day')
				: dateMoment.clone().add(1, 'day').startOf('day');
		}
		return dateMoment.clone();
	};

	const handlePrevious = () => {
		setSelectedDate(prev => navigateDate(prev, 'prev', viewMode));
		setSelectedDay(null);
	};

	const handleNext = () => {
		setSelectedDate(prev => navigateDate(prev, 'next', viewMode));
		setSelectedDay(null);
	};

	const handleDayClick = (day) => {
		const dayMoment = moment.isMoment(day) ? day.clone() : moment.utc(day);
		const dayStart = dayMoment.clone().startOf('day');
		const dayEnd = dayMoment.clone().endOf('day');

		// Get events for the day, respecting the active event type filters
		const dayEvents = filteredEvents.filter(event => {
			const eventDate = moment.utc(event.date);
			return eventDate.isSameOrAfter(dayStart, 'day') && eventDate.isSameOrBefore(dayEnd, 'day');
		});

		setDayModalEvents(dayEvents);
		setDayModalDate(dayMoment.toDate());
		setShowDayModal(true);
		setSelectedDay(dayMoment.clone());
	};

	// Month view only: selecting a day updates the side detail panel instead
	// of opening CalendarDayModal (which stays reserved for week/day view).
	const handleMonthDayClick = (day) => {
		setSelectedDay(moment.isMoment(day) ? day.clone() : moment.utc(day));
	};

	const selectedDayEvents = useMemo(() => {
		if (!selectedDay) return [];
		const dayStart = moment.utc(selectedDay).startOf('day');
		const dayEnd = moment.utc(selectedDay).endOf('day');
		return filteredEvents.filter(event => {
			const eventDate = moment.utc(event.date);
			return eventDate.isSameOrAfter(dayStart, 'day') && eventDate.isSameOrBefore(dayEnd, 'day');
		});
	}, [selectedDay, filteredEvents]);

	const handleEventTypeFilterChange = (eventType, checked) => {
		setEventTypeFilters(prev => ({
			...prev,
			[eventType]: checked,
		}));
	};

	const triggerRefresh = () => setRefreshKey(k => k + 1);

	const handleOpenCreateGoogleEvent = (date) => {
		setEditingGoogleEvent(null);
		setCreateEventDefaultDate(date || null);
		setShowGoogleEventModal(true);
	};

	const handleEditGoogleEvent = (event) => {
		// Reconstruct the raw Google event shape GoogleEventModal expects from the
		// processed calendar event (see the 'google-event' mapping in calendarUtils.js).
		setEditingGoogleEvent({
			id: event.entityId,
			summary: event.title,
			description: event.description,
			start: event.metadata?.start,
			end: event.metadata?.end,
		});
		setShowGoogleEventModal(true);
	};

	const handleDeleteGoogleEvent = async (event) => {
		if (!window.confirm(t('msg.confirmDeleteEvent').replace('{title}', event.title))) return;
		try {
			await GoogleCalendarService.deleteEvent(event.entityId, selectedCalendarId || 'primary');
			toast.success('Evento eliminado');
			setShowDayModal(false);
			triggerRefresh();
		} catch (err) {
			toast.error(getError(err));
		}
	};

	const handleEditAppEvent = (event) => {
		setEditingAppEvent(event);
		setShowAppEventDateModal(true);
	};

	const totalEventsCount = filteredEvents.length;

	const getViewTitle = () => {
		if (viewMode === 'month') {
			return moment.utc(selectedDate).format('MMMM YYYY');
		} else if (viewMode === 'week') {
			const weekStart = moment.utc(selectedDate).startOf('week');
			const weekEnd = moment.utc(selectedDate).endOf('week');
			if (weekStart.month() === weekEnd.month()) {
				return `${weekStart.format('D')} - ${weekEnd.format('D [de] MMMM [de] YYYY')}`;
			} else {
				return `${weekStart.format('D [de] MMMM')} - ${weekEnd.format('D [de] MMMM [de] YYYY')}`;
			}
		} else if (viewMode === 'day') {
			return moment.utc(selectedDate).format('DD [de] MMMM [de] YYYY');
		}
		return '';
	};

	const getNavigationTitle = () => {
		if (viewMode === 'month') {
			return 'Mes anterior';
		} else if (viewMode === 'week') {
			return 'Semana anterior';
		} else if (viewMode === 'day') {
			return 'Día anterior';
		}
		return 'Anterior';
	};

	const getNextTitle = () => {
		if (viewMode === 'month') {
			return 'Mes siguiente';
		} else if (viewMode === 'week') {
			return 'Semana siguiente';
		} else if (viewMode === 'day') {
			return 'Día siguiente';
		}
		return 'Siguiente';
	};

	if (error) {
		return (
			<Card className='calendar-card'>
				<Card.Header>
					<h4 className='mb-0'>Calendario {getViewTitle()}</h4>
				</Card.Header>
				<Card.Body>
					<div className='alert alert-danger'>{error}</div>
				</Card.Body>
			</Card>
		);
	}

	// Legend chips for the month-view header — 4 fixed category dots plus a
	// 5th, conditional Google chip (see calendarEventTypes.CATEGORY_META).
	const legendCategories = [1, 2, 3, 4, ...(googleConnected ? ['google'] : [])];

	return (
		<Card className='calendar-card calendar-fixed-size'>
			<Card.Header className='tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:py-3'>
				<span className='tw:text-[13px] tw:font-semibold tw:text-ink-0'>Calendario de actividad</span>
				<div className='tw:flex tw:items-center tw:gap-2'>
					<button
						type='button'
						className='tw:w-7 tw:h-7 tw:rounded-token tw:flex tw:items-center tw:justify-center tw:text-ink-2 tw:hover:bg-surface-2 tw:disabled:opacity-40 tw:bg-transparent tw:border-0'
						onClick={handlePrevious}
						title={getNavigationTitle()}
						disabled={loading}
					>
						<FaChevronLeft size={12} />
					</button>
					<span className='tw:text-[13px] tw:font-medium tw:text-ink-0 tw:min-w-[140px] tw:text-center'>
						{getViewTitle()}
					</span>
					<button
						type='button'
						className='tw:w-7 tw:h-7 tw:rounded-token tw:flex tw:items-center tw:justify-center tw:text-ink-2 tw:hover:bg-surface-2 tw:disabled:opacity-40 tw:bg-transparent tw:border-0'
						onClick={handleNext}
						title={getNextTitle()}
						disabled={loading}
					>
						<FaChevronRight size={12} />
					</button>
					<button
						type='button'
						className='tw:h-7 tw:px-2.5 tw:rounded-token tw:border tw:border-border-strong tw:text-[12.5px] tw:font-medium tw:text-ink-0 tw:hover:bg-surface-2 tw:bg-surface'
						onClick={() => {
							setSelectedDate(adjustDateForViewMode(moment.utc(), viewMode));
							setSelectedDay(moment.utc().startOf('day'));
						}}
					>
						Hoy
					</button>
				</div>
				<div className='tw:flex tw:items-center tw:gap-3 tw:flex-wrap'>
					{legendCategories.map((category) => (
						<span key={category} className='tw:flex tw:items-center tw:gap-1.5 tw:text-[11.5px] tw:text-ink-2'>
							<span className={classnames('tw:w-2 tw:h-2 tw:rounded-[2px]', CATEGORY_META[category].className)} />
							{CATEGORY_META[category].label}
						</span>
					))}
				</div>
			</Card.Header>
			<Card.Body className='calendar-body-container' style={{ position: 'relative' }}>
				{googleConnected && (
					<div className='d-flex justify-content-end mb-3'>
						<Button variant='primary' size='sm' onClick={() => handleOpenCreateGoogleEvent()}>
							Crear evento
						</Button>
					</div>
				)}
				<CalendarFilters
					eventTypeFilters={eventTypeFilters}
					onEventTypeFilterChange={handleEventTypeFilterChange}
					dashboardModules={dashboardModules}
					googleConnected={googleConnected}
				/>
				{viewMode === 'month' ? (
					<div className='tw:grid tw:grid-cols-[1fr_320px] tw:max-[1100px]:grid-cols-1 tw:gap-4'>
						<CalendarGrid
							viewMode={viewMode}
							selectedDate={selectedDate}
							eventsByDay={loading ? {} : eventsByDay}
							selectedDay={selectedDay}
							onDayClick={handleMonthDayClick}
							loading={loading}
						/>
						<CalendarDetailPanel
							day={selectedDay}
							events={selectedDayEvents}
							onEditGoogleEvent={handleEditGoogleEvent}
							onDeleteGoogleEvent={handleDeleteGoogleEvent}
							onEditAppEvent={handleEditAppEvent}
							onAddEvent={(day) => handleOpenCreateGoogleEvent(day)}
							googleConnected={googleConnected}
						/>
					</div>
				) : (
					<CalendarGrid
						viewMode={viewMode}
						selectedDate={selectedDate}
						eventsByDay={loading ? {} : eventsByDay}
						selectedDay={selectedDay}
						onDayClick={handleDayClick}
						loading={loading}
					/>
				)}
				{viewMode === 'month' && (
					<div className='tw:mt-3 tw:text-[12px] tw:text-ink-3'>
						{loading ? '—' : `${totalEventsCount} evento${totalEventsCount !== 1 ? 's' : ''}`} este mes
					</div>
				)}
				<CalendarDayModal
					show={showDayModal}
					onHide={() => {
						setShowDayModal(false);
						setSelectedDay(null);
					}}
					day={dayModalDate}
					events={dayModalEvents}
					onEditGoogleEvent={handleEditGoogleEvent}
					onDeleteGoogleEvent={handleDeleteGoogleEvent}
					onEditAppEvent={handleEditAppEvent}
				/>
				<GoogleEventModal
					show={showGoogleEventModal}
					onHide={() => setShowGoogleEventModal(false)}
					calendarId={selectedCalendarId || 'primary'}
					editingEvent={editingGoogleEvent}
					defaultDate={createEventDefaultDate}
					onSaved={() => {
						setEditingGoogleEvent(null);
						// dayModalEvents is a snapshot taken when the day was clicked — it won't
						// pick up the edited date/title on its own, so close the day modal
						// (same as the delete flow) instead of leaving stale data on screen.
						setShowDayModal(false);
						triggerRefresh();
					}}
				/>
				<AppEventDateModal
					show={showAppEventDateModal}
					onHide={() => setShowAppEventDateModal(false)}
					event={editingAppEvent}
					onSaved={() => {
						setShowDayModal(false);
						triggerRefresh();
					}}
				/>
			</Card.Body>
		</Card>
	);
}

export default CalendarView;
